import { useMutation, useQuery } from "@tanstack/react-query";
import PurchaserClient from "../PurchaserClient";
import { queryClient } from "./client";
import type { Requisition, RequisitionListResponse, RequisitionStatus } from "../Purchaser/Requisitions";

const USE_MOCK = import.meta.env["VITE_USE_MOCK_REQUISITIONS"] === "true";

const getMockRequisitions = async () => {
    const { MOCK_REQUISITIONS } = await import("./mockRequisitions");
    return MOCK_REQUISITIONS;
};

export const QueryOptions = {
    RequisitionList: (filters?: { status?: RequisitionStatus; search?: string; page?: number; limit?: number }) => ({
        queryKey: ["requisitionList", filters],
        queryFn: async (): Promise<RequisitionListResponse> => {
            if (USE_MOCK) {
                let results = await getMockRequisitions();
                if (filters?.status) results = results.filter((r) => r.status === filters.status);
                if (filters?.search) {
                    const q = filters.search.toLowerCase();
                    results = results.filter(
                        (r) =>
                            r.subject?.toLowerCase().includes(q) ||
                            r.requesterName?.toLowerCase().includes(q) ||
                            r.requesterEmail.toLowerCase().includes(q),
                    );
                }
                return { results };
            }
            return PurchaserClient.getRequisitions(filters);
        },
    }),
    Requisition: (id: string | number) => ({
        queryKey: ["requisition", id],
        queryFn: async (): Promise<Requisition> => {
            if (USE_MOCK) {
                const mocks = await getMockRequisitions();
                const req = mocks.find((r) => r.id === String(id));
                if (!req) throw new Error("Requisition not found");
                return req;
            }
            return PurchaserClient.getRequisition(id);
        },
        enabled: !!id,
    }),
};

export function useRequisitionList(filters?: { status?: RequisitionStatus; search?: string; page?: number; limit?: number }) {
    return useQuery(QueryOptions.RequisitionList(filters));
}

export function useRequisition(id: string | number) {
    return useQuery(QueryOptions.Requisition(id));
}

export function invalidateRequisitionListQueries() {
    queryClient.invalidateQueries({ queryKey: ["requisitionList"] });
}

export function invalidateRequisitionQueries(id?: string | number) {
    if (id) {
        queryClient.invalidateQueries({ queryKey: ["requisition", id] });
    } else {
        queryClient.invalidateQueries({ queryKey: ["requisition"] });
    }
}

export function useClaimRequisition() {
    return useMutation({
        mutationFn: (id: string | number) => PurchaserClient.claimRequisition(id),
        onSuccess: (_data, id) => {
            invalidateRequisitionQueries(id);
            invalidateRequisitionListQueries();
        },
    });
}

export function useAssignRequisition() {
    return useMutation({
        mutationFn: ({ id, userId, explanation }: { id: string | number; userId: number | null; explanation: string }) =>
            PurchaserClient.assignRequisition(id, { userId, explanation }),
        onSuccess: (_data, { id }) => {
            invalidateRequisitionQueries(id);
            invalidateRequisitionListQueries();
        },
    });
}

export function useReplyToRequisition() {
    return useMutation({
        mutationFn: ({ id, body, senderId }: { id: string | number; body: string; senderId: number }) =>
            PurchaserClient.replyToRequisition(id, { body, senderId }),
        onSuccess: (_data, { id }) => {
            invalidateRequisitionQueries(id);
            invalidateRequisitionListQueries();
        },
    });
}

export function useUpdateRequisitionSubject() {
    return useMutation({
        mutationFn: ({ id, subject }: { id: string | number; subject: string }) =>
            PurchaserClient.updateRequisition(id, { subject }),
        onSuccess: (_data, { id }) => {
            invalidateRequisitionQueries(id);
            invalidateRequisitionListQueries();
        },
    });
}

export function useConvertToRFQ() {
    return useMutation({
        mutationFn: (id: string | number) => PurchaserClient.convertToRFQ(id),
        onSuccess: (_data, id) => {
            invalidateRequisitionQueries(id);
            invalidateRequisitionListQueries();
        },
    });
}
